import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type UserSearchResult } from "../../api/client";
import UserPicker from "../../components/UserPicker";

export default function RolesAdmin() {
  const qc = useQueryClient();
  const { data: roles, isLoading, error: loadError } = useQuery({
    queryKey: ["admin-roles"],
    queryFn: api.admin.listRoles,
  });

  const [openRole, setOpenRole] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ["admin-role-members", openRole],
    queryFn: () => api.admin.listRoleMembers(openRole!),
    enabled: !!openRole,
  });

  const [picked, setPicked] = useState<UserSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => api.admin.addRoleMember(openRole!, picked!.oid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-role-members", openRole] });
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
      setPicked(null);
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  const remove = useMutation({
    mutationFn: (oid: string) => api.admin.removeRoleMember(openRole!, oid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-role-members", openRole] });
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    },
  });

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Roles</h1>
        <p className="text-slate-600 mt-1">
          Add or remove staff from the four security groups that gate Approvals, Payroll, Finance, and Admin.
          Backed by Microsoft Entra ID — changes propagate to the user's next sign-in.
        </p>
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          <div className="font-medium">Failed to load roles</div>
          <div className="font-mono text-xs mt-1">{(loadError as Error).message}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading && <div className="text-slate-500">Loading…</div>}
        {roles?.map((r) => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-liaise-navy">{r.label}</div>
                <div className="text-slate-600 text-sm mt-1">{r.description}</div>
              </div>
              <span className="text-2xl font-semibold text-liaise-navy">
                {r.member_count ?? "—"}
              </span>
            </div>
            {r.error && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {r.error}
              </div>
            )}
            <button
              onClick={() => { setOpenRole(r.key); setPicked(null); setError(null); }}
              className="mt-4 text-liaise-navy text-sm font-medium hover:underline"
            >
              Manage members →
            </button>
          </div>
        ))}
      </div>

      {/* Members modal */}
      {openRole && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => setOpenRole(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-liaise-navy">
                {members.data?.label ?? roles?.find((r) => r.key === openRole)?.label}
              </h2>
              <button onClick={() => setOpenRole(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>

            <div className="px-6 py-4 border-b border-slate-200 space-y-3">
              <div className="text-sm font-medium text-slate-700">Add a member</div>
              <UserPicker
                selected={picked}
                onChange={setPicked}
                placeholder="Search Liaise users by name or email…"
              />
              {error && <div className="text-red-600 text-sm">{error}</div>}
              <button
                onClick={() => add.mutate()}
                disabled={!picked || add.isPending}
                className="bg-liaise-navy text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
              >
                {add.isPending ? "Adding…" : "Add to role"}
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {members.isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading members…</div>}
              {members.error && <div className="px-6 py-12 text-red-600 text-sm">{(members.error as Error).message}</div>}
              {members.data && members.data.members.length === 0 && (
                <div className="px-6 py-12 text-center text-slate-500">No members yet.</div>
              )}
              {members.data && members.data.members.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left sticky top-0">
                    <tr>
                      <th className="px-6 py-3 font-medium">Member</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {members.data.members.map((m) => (
                      <tr key={m.oid}>
                        <td className="px-6 py-3">
                          <div className="font-medium text-slate-700">{m.displayName}</div>
                          <div className="text-xs text-slate-500">{m.email}</div>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => remove.mutate(m.oid)}
                            disabled={remove.isPending}
                            className="text-red-600 text-xs hover:underline disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
